import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ChevronLeft, ChevronRight } from 'lucide-react';

// ─── Brand Colors (matching marketing site globals.css) ───
// honey: #F5A623, honey-light: #FFD700, amber: #B8860B
// charcoal: #2D3748, charcoal-light: #4A5568
// cream: #FFFEF5, sky-light: #87CEEB

const IMAGES_BASE = 'https://bizbuzzapp.com/images';

// ─── Cloud SVGs (matching marketing site) ───
function Cloud({ className, style }: { className?: string; style?: React.CSSProperties }) {
  return (
    <svg className={className} style={style} viewBox="0 0 200 80" fill="white" xmlns="http://www.w3.org/2000/svg">
      <ellipse cx="60" cy="55" rx="60" ry="22" /><ellipse cx="100" cy="55" rx="55" ry="18" /><ellipse cx="145" cy="55" rx="50" ry="20" />
      <ellipse cx="55" cy="40" rx="35" ry="28" /><ellipse cx="100" cy="35" rx="40" ry="32" /><ellipse cx="140" cy="42" rx="30" ry="25" />
    </svg>
  );
}

function SmallCloud({ className, style }: { className?: string; style?: React.CSSProperties }) {
  return (
    <svg className={className} style={style} viewBox="0 0 140 55" fill="white" xmlns="http://www.w3.org/2000/svg">
      <ellipse cx="45" cy="38" rx="42" ry="15" /><ellipse cx="90" cy="38" rx="38" ry="13" />
      <ellipse cx="40" cy="26" rx="25" ry="20" /><ellipse cx="75" cy="24" rx="28" ry="22" /><ellipse cx="105" cy="30" rx="22" ry="18" />
    </svg>
  );
}

function ThinCloud({ className, style }: { className?: string; style?: React.CSSProperties }) {
  return (
    <svg className={className} style={style} viewBox="0 0 180 40" fill="white" xmlns="http://www.w3.org/2000/svg">
      <ellipse cx="50" cy="25" rx="48" ry="12" /><ellipse cx="110" cy="25" rx="45" ry="10" /><ellipse cx="155" cy="26" rx="25" ry="9" />
      <ellipse cx="80" cy="18" rx="30" ry="15" /><ellipse cx="130" cy="20" rx="25" ry="13" />
    </svg>
  );
}

// ─── Checkmark ───
function CheckIcon() {
  return (
    <svg className="w-4 h-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
    </svg>
  );
}

function HoneyCheck() {
  return (
    <svg className="w-5 h-5 text-[#F5A623] shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
    </svg>
  );
}

// ─── HexCard (matching marketing site: 200x230 viewBox, aspect-ratio) ───
function HexCard({ children }: { children: React.ReactNode }) {
  return (
    <div className="relative w-full max-w-[280px] mx-auto" style={{ aspectRatio: '200 / 230' }}>
      <svg viewBox="0 0 200 230" className="absolute inset-0 w-full h-full" xmlns="http://www.w3.org/2000/svg">
        <polygon points="100,2 196,58 196,172 100,228 4,172 4,58" fill="white" stroke="#F5A623" strokeWidth="2" />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center px-8 py-12 text-center">
        {children}
      </div>
    </div>
  );
}

// ─── OptImage (picture with WebP + PNG fallback) ───
function OptImage({ src, alt, width, height, className }: { src: string; alt: string; width: number; height: number; className?: string }) {
  const webpSrc = src.replace(/\.png$/, '.webp');
  return (
    <picture>
      <source srcSet={webpSrc} type="image/webp" />
      <img src={src} alt={alt} width={width} height={height} className={className} loading="lazy" />
    </picture>
  );
}

// ─── HERO ───
function Hero() {
  return (
    <section className="relative min-h-[80vh] sm:min-h-[90vh] flex items-center overflow-hidden">
      <div className="absolute inset-0 overflow-hidden pointer-events-none opacity-100">
        <div className="absolute inset-0 bg-gradient-to-b from-[#87CEEB] via-[#B8DFF5] to-white" />
        <Cloud className="absolute w-[220px] sm:w-[280px] top-[8%] left-[2%] animate-float-cloud opacity-95" />
        <SmallCloud className="absolute w-[160px] sm:w-[200px] top-[12%] right-[5%] animate-float-cloud-slow opacity-90" />
        <ThinCloud className="absolute w-[140px] sm:w-[180px] top-[28%] left-[35%] animate-float-cloud opacity-60" style={{ animationDelay: '5s' }} />
        <Cloud className="absolute w-[200px] sm:w-[260px] top-[50%] right-[15%] animate-float-cloud-slow opacity-50" style={{ animationDelay: '8s' }} />
        <ThinCloud className="absolute w-[120px] sm:w-[160px] top-[4%] left-[50%] animate-float-cloud opacity-55" style={{ animationDelay: '12s' }} />
        <SmallCloud className="absolute w-[100px] sm:w-[140px] top-[40%] left-[8%] animate-float-cloud-slow opacity-40" style={{ animationDelay: '3s' }} />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 sm:py-32 lg:py-40">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div className="text-center lg:text-left">
            <div className="inline-flex items-center gap-2 bg-white/80 backdrop-blur-sm px-4 py-2 rounded-full mb-6 border border-[#F5A623]/30">
              <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
              <span className="text-sm font-medium text-[#2D3748]">Trusted by professionals in real estate, finance, and tech</span>
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight mb-6">
              <span className="text-[#2D3748]">Your clients forget</span><br />
              <span className="text-[#2D3748]">you exist.</span><br />
              <span className="bg-gradient-to-r from-[#F5A623] to-[#B8860B] bg-clip-text text-transparent">Fix that in 5 minutes a week.</span>
            </h1>

            <p className="text-lg sm:text-xl text-[#4A5568] mb-8 max-w-xl mx-auto lg:mx-0 leading-relaxed">
              BizBuzz finds the news your audience cares about, writes your newsletter, and sends it — so you stay top-of-mind without the time sink.
            </p>

            <div className="flex flex-col sm:flex-row items-center gap-4 justify-center lg:justify-start">
              <Link to="/signup" className="inline-flex items-center justify-center whitespace-nowrap px-5 sm:px-8 py-3 sm:py-4 bg-gradient-to-r from-[#F5A623] to-[#FFD700] text-[#2D3748] font-bold rounded-full text-base sm:text-lg hover:shadow-xl hover:shadow-[#F5A623]/25 transition-all duration-300 hover:-translate-y-0.5">
                Start Your Newsletter — $29/mo
                <ArrowRight className="ml-2 w-5 h-5 shrink-0" />
              </Link>
              <a href="#how-it-works" className="inline-flex items-center justify-center whitespace-nowrap px-5 sm:px-8 py-3 sm:py-4 bg-white/60 backdrop-blur-sm text-[#2D3748] font-semibold rounded-full text-base sm:text-lg border border-[#2D3748]/10 hover:bg-white transition-all duration-300">
                See How It Works
              </a>
            </div>

            <div className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-3 justify-center lg:justify-start text-sm text-[#4A5568]">
              <div className="flex items-center gap-1.5"><CheckIcon /> 30-day money-back guarantee</div>
              <div className="flex items-center gap-1.5"><CheckIcon /> Cancel anytime</div>
              <div className="flex items-center gap-1.5"><CheckIcon /> First newsletter in under an hour</div>
            </div>
          </div>

          <div className="hidden lg:flex items-center justify-center">
            <div className="relative">
              <div className="absolute inset-0 bg-[#F5A623]/20 rounded-full blur-3xl scale-75" />
              <div className="relative animate-bee-hover">
                <OptImage src={`${IMAGES_BASE}/logo-full.png`} alt="Biz Bee mascot" width={320} height={320} className="drop-shadow-2xl" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── PROBLEM SECTION ───
const problems = [
  { title: "I don't have time to write a newsletter", description: "You're running a business. Writing 500 words about industry news every week isn't happening. So it doesn't.", image: `${IMAGES_BASE}/gen/problem-no-time.png`, alt: 'Exhausted bee at blank computer screen' },
  { title: 'I tried Mailchimp. Then I gave up.', description: "You signed up for a sending tool but the blank template stared back at you. You need content, not just a canvas.", image: `${IMAGES_BASE}/gen/problem-tried-tools.png`, alt: 'Bee overwhelmed by app icons' },
  { title: 'Out of sight, out of mind', description: "Without consistent communication, your network forgets you exist. Referrals dry up. Competitors move in.", image: `${IMAGES_BASE}/gen/problem-forgotten.png`, alt: 'Lonely bee waving as people walk away' },
];

function ProblemSection() {
  return (
    <section className="relative py-20 sm:py-28 bg-gradient-to-b from-white to-[#FFFEF5] overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <p className="text-[#F5A623] font-semibold mb-3 text-sm uppercase tracking-wide">The Problem</p>
          <h2 className="text-3xl sm:text-4xl font-bold text-[#2D3748] mb-4">Creating Newsletters Shouldn&apos;t Feel Like a Second Job</h2>
          <p className="text-lg text-[#4A5568] max-w-2xl mx-auto">
            You know staying top-of-mind matters. But the effort to find content, write it up, and send it? That&apos;s where most professionals give up.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-start max-w-5xl mx-auto">
          {problems.map((problem, index) => (
            <div key={index} className="flex flex-col items-center group">
              <div className="w-44 h-44 min-h-[176px] -mb-16 relative z-10 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                <OptImage src={problem.image} alt={problem.alt} width={176} height={176} className="object-contain drop-shadow-lg" />
              </div>
              <HexCard>
                <h3 className="text-lg font-bold text-[#2D3748] mb-2 mt-6">{problem.title}</h3>
                <p className="text-[#4A5568] leading-relaxed text-sm">{problem.description}</p>
              </HexCard>
            </div>
          ))}
        </div>

        <div className="text-center mt-14 flex items-center justify-center gap-3">
          <img src={`${IMAGES_BASE}/logo-192.png`} alt="" width={75} height={75} className="animate-bee-hover" />
          <p className="text-xl font-semibold text-[#2D3748]">
            What if it took <span className="text-[#F5A623]">5 minutes instead of 5 hours?</span>
          </p>
        </div>
      </div>
    </section>
  );
}

// ─── FEATURES CAROUSEL ───
const features = [
  { title: 'Industry News on Autopilot', description: 'Tell BizBuzz your industry, and it finds the best RSS feeds, subreddits, and news sources automatically. No manual hunting required.', image: `${IMAGES_BASE}/gen/feature-news-autopilot.png` },
  { title: 'Written in Your Voice', description: 'Your newsletter sounds like you wrote it. BizBuzz learns your writing style and generates commentary that matches your unique tone.', image: `${IMAGES_BASE}/gen/feature-your-voice.png` },
  { title: "Know Who's Reading", description: 'See which contacts are engaged and which are going cold. Engagement scoring helps you focus your follow-ups where they matter.', image: `${IMAGES_BASE}/gen/feature-engagement.png` },
  { title: 'Review Before It Sends', description: "Every newsletter goes through you first. Review, edit, approve — then send. You're always in control.", image: `${IMAGES_BASE}/gen/feature-review.png` },
  { title: 'Send From Anywhere', description: 'Review and approve your newsletter from your phone. The whole process takes 5 minutes, wherever you are.', image: `${IMAGES_BASE}/gen/feature-mobile.png` },
  { title: 'One Click, Done', description: 'Approve your articles and hit generate. A polished, professional newsletter appears — ready to send to your audience.', image: `${IMAGES_BASE}/gen/feature-one-click.png` },
];

function FeaturesSection() {
  const [current, setCurrent] = useState(0);
  const [paused, setPaused] = useState(false);
  const [fading, setFading] = useState(false);

  const fadeTo = useCallback((getNext: (c: number) => number) => {
    setFading(true);
    setTimeout(() => { setCurrent(getNext); setFading(false); }, 300);
  }, []);

  const goPrev = useCallback(() => fadeTo((c) => (c === 0 ? features.length - 1 : c - 1)), [fadeTo]);
  const goNext = useCallback(() => fadeTo((c) => (c === features.length - 1 ? 0 : c + 1)), [fadeTo]);

  useEffect(() => {
    if (paused) return;
    const timer = setInterval(() => { fadeTo((c) => (c === features.length - 1 ? 0 : c + 1)); }, 5000);
    return () => clearInterval(timer);
  }, [paused, fadeTo]);

  const handleManual = (fn: () => void) => { fn(); setPaused(true); setTimeout(() => setPaused(false), 15000); };

  const feature = features[current];

  return (
    <section id="features" className="py-20 sm:py-28 bg-[#FFFEF5]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <p className="text-[#F5A623] font-semibold mb-3 text-sm uppercase tracking-wide">Features</p>
          <h2 className="text-3xl sm:text-4xl font-bold text-[#2D3748] mb-4">Everything You Need, Nothing You Don&apos;t</h2>
          <p className="text-lg text-[#4A5568] max-w-2xl mx-auto">
            From finding the right content to sending polished newsletters, BizBuzz handles the heavy lifting so you can focus on what matters.
          </p>
        </div>

        <div className="flex items-start justify-center gap-0 lg:gap-8">
          <div className="relative w-full max-w-md">
            <div className="flex flex-col items-center relative">
              <div className={`w-36 h-36 sm:w-44 sm:h-44 -mb-16 relative z-10 flex items-center justify-center transition-opacity duration-300 ${fading ? 'opacity-0' : 'opacity-100'}`}>
                <OptImage src={feature.image} alt={feature.title} width={160} height={160} className="object-contain drop-shadow-lg" />
              </div>

              <div className="relative w-full flex items-center">
                <button onClick={() => handleManual(goPrev)} className="hidden md:flex items-center justify-center w-12 h-12 rounded-full bg-white border-2 border-[#F5A623]/30 hover:border-[#F5A623] hover:shadow-md transition-all text-[#F5A623] shrink-0 -ml-2 mr-4" aria-label="Previous feature">
                  <ChevronLeft className="w-6 h-6" />
                </button>

                <div className={`w-full transition-opacity duration-300 ${fading ? 'opacity-0' : 'opacity-100'}`}>
                  <div className="max-w-[280px] mx-auto">
                    <HexCard>
                      <h3 className="text-lg font-bold text-[#2D3748] mb-2 mt-6">{feature.title}</h3>
                      <p className="text-[#4A5568] leading-relaxed text-sm">{feature.description}</p>
                    </HexCard>
                  </div>
                </div>

                <button onClick={() => handleManual(goNext)} className="hidden md:flex items-center justify-center w-12 h-12 rounded-full bg-white border-2 border-[#F5A623]/30 hover:border-[#F5A623] hover:shadow-md transition-all text-[#F5A623] shrink-0 -mr-2 ml-4" aria-label="Next feature">
                  <ChevronRight className="w-6 h-6" />
                </button>
              </div>
            </div>

            {/* Mobile nav */}
            <div className="flex md:hidden justify-center gap-4 mt-4">
              <button onClick={() => handleManual(goPrev)} className="w-10 h-10 rounded-full bg-white border-2 border-[#F5A623]/30 flex items-center justify-center text-[#F5A623]" aria-label="Previous"><ChevronLeft className="w-5 h-5" /></button>
              <button onClick={() => handleManual(goNext)} className="w-10 h-10 rounded-full bg-white border-2 border-[#F5A623]/30 flex items-center justify-center text-[#F5A623]" aria-label="Next"><ChevronRight className="w-5 h-5" /></button>
            </div>

            {/* Dots */}
            <div className="flex justify-center gap-2 mt-6">
              {features.map((_, i) => (
                <button key={i} onClick={() => handleManual(() => fadeTo(() => i))} className={`w-3 h-3 rounded-full transition-all duration-300 ${i === current ? 'bg-[#F5A623] scale-125' : 'bg-[#F5A623]/30 hover:bg-[#F5A623]/50'}`} aria-label={`Go to feature ${i + 1}`} />
              ))}
            </div>
          </div>

          {/* Bee wand - desktop */}
          <div className="hidden lg:flex flex-col items-center shrink-0 pt-16">
            <div className="transition-transform duration-300">
              <OptImage src={`${IMAGES_BASE}/bee-wand-v2-nobg.png`} alt="Biz Bee with magic wand" width={260} height={260} className="object-contain drop-shadow-xl" />
            </div>
          </div>
        </div>

        {/* Bee wand - mobile */}
        <div className="flex lg:hidden justify-center mt-8">
          <OptImage src={`${IMAGES_BASE}/bee-wand-v2-nobg.png`} alt="Biz Bee with magic wand" width={180} height={180} className="object-contain drop-shadow-xl" />
        </div>
      </div>
    </section>
  );
}

// ─── HOW IT WORKS ───
const steps = [
  { number: '01', title: 'Tell Us Your Industry', description: 'Pick your field and BizBuzz finds the best news sources automatically — or add your own favorites. Setup takes minutes, not hours.', image: `${IMAGES_BASE}/step-connect-nobg.png`, alt: 'Bee connecting news sources' },
  { number: '02', title: 'Review Your Newsletter', description: "Your curated feed fills with categorized articles. Keep the ones you like, skip the rest. Five minutes is all it takes.", image: `${IMAGES_BASE}/step-review-nobg.png`, alt: 'Bee reviewing content' },
  { number: '03', title: 'Hit Send', description: 'One click generates a polished newsletter in your voice. Send it to your audience and get back to work.', image: `${IMAGES_BASE}/step-send-nobg.png`, alt: 'Bee sending newsletter' },
];

function HowItWorks() {
  return (
    <section id="how-it-works" className="py-20 sm:py-28 bg-white relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center mb-16">
          <p className="text-[#F5A623] font-semibold mb-3 text-sm uppercase tracking-wide">How It Works</p>
          <h2 className="text-3xl sm:text-4xl font-bold text-[#2D3748] mb-4">From the Hive to the Inbox in Three Simple Steps</h2>
          <p className="text-lg text-[#4A5568] max-w-2xl mx-auto">No technical skills needed. If you can swipe on your phone, you can create professional newsletters.</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-start max-w-5xl mx-auto">
          {steps.map((step, index) => (
            <div key={index} className="flex flex-col items-center group">
              <div className="w-44 h-44 min-h-[176px] -mb-16 relative z-10 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                <OptImage src={step.image} alt={step.alt} width={176} height={176} className="object-contain drop-shadow-lg" />
              </div>
              <HexCard>
                <div className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-gradient-to-br from-[#F5A623] to-[#FFD700] text-[#2D3748] font-bold text-sm mb-2 mt-4">
                  {step.number}
                </div>
                <h3 className="text-lg font-bold text-[#2D3748] mb-2">{step.title}</h3>
                <p className="text-[#4A5568] leading-relaxed text-sm">{step.description}</p>
              </HexCard>
            </div>
          ))}
        </div>

        <div className="relative mt-8 hidden md:flex justify-center">
          <div className="animate-bee-hover" style={{ animationDelay: '0.5s' }}>
            <img src={`${IMAGES_BASE}/logo-192.png`} alt="" width={75} height={75} className="drop-shadow-md" />
          </div>
          <div className="absolute top-1/2 left-[20%] right-[20%] h-px bg-gradient-to-r from-transparent via-[#F5A623]/30 to-transparent -translate-y-1/2 -z-10" />
        </div>
      </div>
    </section>
  );
}

// ─── BUILT FOR ───
const industries = [
  { title: 'Real Estate Agents', description: 'Stay top-of-mind with past clients. Market updates, mortgage rates, and local news — curated and sent automatically.', image: `${IMAGES_BASE}/gen/industry-real-estate.png` },
  { title: 'Financial Advisors', description: 'Keep clients informed during market moves. Curated financial news with a review-before-send workflow your compliance team will love.', image: `${IMAGES_BASE}/gen/industry-finance.png` },
  { title: 'Tech Freelancers', description: 'Turn your daily Hacker News habit into a newsletter that attracts clients. RSS, Reddit, Medium — all in one feed.', image: `${IMAGES_BASE}/gen/industry-tech.png` },
];

function BuiltFor() {
  return (
    <section className="py-20 sm:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <p className="text-[#F5A623] font-semibold mb-3 text-sm uppercase tracking-wide">Built For Your Industry</p>
          <h2 className="text-3xl sm:text-4xl font-bold text-[#2D3748] mb-4">BizBuzz Knows Where Your Audience&apos;s News Lives</h2>
          <p className="text-lg text-[#4A5568] max-w-2xl mx-auto">Pre-configured source discovery for your industry. Tell BizBuzz what you do, and it delivers relevant content to your audience&apos;s inbox in your voice.</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-start max-w-5xl mx-auto">
          {industries.map((industry, index) => (
            <div key={index} className="flex flex-col items-center group">
              <div className="w-44 h-44 min-h-[176px] -mb-16 relative z-10 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                <OptImage src={industry.image} alt={industry.title} width={176} height={176} className="object-contain drop-shadow-lg" />
              </div>
              <HexCard>
                <h3 className="text-lg font-bold text-[#2D3748] mb-2 mt-6">{industry.title}</h3>
                <p className="text-[#4A5568] leading-relaxed text-sm">{industry.description}</p>
              </HexCard>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── PRICING ───
const plans = [
  { name: 'Starter', tagline: 'Everything you need to start', price: '$29', period: '/month', featured: false, features: ['1 audience', '15 content sources', '2,500 subscribers', 'Unlimited newsletters', 'Scheduled sends', 'Full rich-text editor', '30-day money-back guarantee'] },
  { name: 'Growth', tagline: 'For growing businesses', price: '$79', period: '/month', featured: true, features: ['5 audiences', '75 content sources', '10,000 subscribers', 'Unlimited newsletters', 'Scheduled sends', 'Full rich-text editor', 'Engagement scoring', '30-day money-back guarantee'] },
  { name: 'Pro', tagline: 'For serious operators', price: '$149', period: '/month', featured: false, features: ['10 audiences', '150 content sources', '20,000 subscribers', 'Unlimited newsletters', 'Scheduled sends', 'Full rich-text editor', 'Engagement scoring', 'Priority support', '30-day money-back guarantee'] },
];

const comparison = [
  { feature: 'Audiences', starter: '1', growth: '5', pro: '10' },
  { feature: 'Content sources', starter: '15', growth: '75', pro: '150' },
  { feature: 'Subscribers', starter: '2,500', growth: '10,000', pro: '20,000' },
  { feature: 'Newsletters', starter: 'Unlimited', growth: 'Unlimited', pro: 'Unlimited' },
  { feature: 'Scheduled sends', starter: '✓', growth: '✓', pro: '✓' },
  { feature: 'Engagement scoring', starter: '—', growth: '✓', pro: '✓' },
  { feature: 'Priority support', starter: '—', growth: '—', pro: '✓' },
];

function PricingSection() {
  return (
    <section id="pricing" className="relative py-20 sm:py-28 bg-white overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center mb-16">
          <p className="text-[#F5A623] font-semibold mb-3 text-sm uppercase tracking-wide">Pricing</p>
          <h2 className="text-3xl sm:text-4xl font-bold text-[#2D3748] mb-4">Simple Pricing That Scales With Your Ambition</h2>
          <p className="text-lg text-[#4A5568] max-w-2xl mx-auto">Full refund if you&apos;re not happy. No hidden fees, no surprise stingers.</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
          {plans.map((plan, index) => (
            <div key={index} className={`relative rounded-2xl p-8 transition-all duration-300 ${plan.featured ? 'bg-gradient-to-b from-[#F5A623]/10 to-[#B8860B]/5 border-2 border-[#F5A623] shadow-lg shadow-[#F5A623]/10 md:scale-105' : 'bg-white border border-gray-200'}`}>
              {plan.featured && (
                <div className="absolute -top-6 md:-top-10 left-1/2 -translate-x-1/2 flex items-center gap-1.5 md:gap-2 whitespace-nowrap">
                  <img src={`${IMAGES_BASE}/logo-192.png`} alt="" width={40} height={40} className="w-10 h-10 md:w-16 md:h-16 drop-shadow-sm -rotate-12 flex-shrink-0" />
                  <span className="bg-gradient-to-r from-[#F5A623] to-[#FFD700] text-[#2D3748] text-xs md:text-sm font-bold px-3 md:px-4 py-1 md:py-1.5 rounded-full shadow-sm">Most Popular</span>
                  <img src={`${IMAGES_BASE}/logo-192.png`} alt="" width={40} height={40} className="w-10 h-10 md:w-16 md:h-16 drop-shadow-sm rotate-12 flex-shrink-0" />
                </div>
              )}
              <h3 className="text-xl font-bold text-[#2D3748]">{plan.name}</h3>
              <p className="text-[#4A5568] text-sm mb-6">{plan.tagline}</p>
              <div className="mb-6">
                <span className="text-4xl font-extrabold text-[#2D3748]">{plan.price}</span>
                <span className="text-[#4A5568] ml-1">{plan.period}</span>
              </div>
              <ul className="space-y-3 mb-8">
                {plan.features.map((feat, i) => (
                  <li key={i} className="flex items-start gap-3"><HoneyCheck /><span className="text-[#4A5568] text-sm">{feat}</span></li>
                ))}
              </ul>
              <Link to="/signup" className={`block w-full text-center py-3 rounded-full font-semibold transition-all duration-300 ${plan.featured ? 'bg-gradient-to-r from-[#F5A623] to-[#FFD700] text-[#2D3748] hover:shadow-lg hover:shadow-[#F5A623]/25 hover:-translate-y-0.5' : 'bg-[#2D3748] text-white hover:opacity-90'}`}>
                Start Your Newsletter
              </Link>
            </div>
          ))}
        </div>

        <div className="text-center mt-12 space-y-4">
          <p className="text-[#4A5568]">All plans include a <strong className="text-[#2D3748]">30-day money-back guarantee</strong>. Not happy? Full refund, no questions asked.</p>
          <p className="text-[#4A5568] text-sm">Other newsletter tools charge $500/mo for what you get starting at $29.</p>
          <p className="text-[#4A5568] text-sm">Need more than 10 audiences? <a href="mailto:sales@bizbuzzapp.com" className="text-[#F5A623] hover:underline font-medium">Let&apos;s talk.</a></p>
        </div>

        {/* Comparison Table - Desktop */}
        <div className="mt-20 hidden md:block">
          <h3 className="text-2xl font-bold text-[#2D3748] text-center mb-8">Feature Comparison</h3>
          <table className="w-full max-w-4xl mx-auto text-left">
            <thead>
              <tr className="border-b-2 border-[#F5A623]/30">
                <th className="py-4 px-4 text-[#2D3748] font-semibold">Feature</th>
                <th className="py-4 px-4 text-center text-[#2D3748] font-semibold">Starter</th>
                <th className="py-4 px-4 text-center text-[#F5A623] font-semibold">Growth</th>
                <th className="py-4 px-4 text-center text-[#2D3748] font-semibold">Pro</th>
              </tr>
            </thead>
            <tbody>
              {comparison.map((row, i) => (
                <tr key={i} className="border-b border-gray-100">
                  <td className="py-3 px-4 text-[#4A5568] text-sm">{row.feature}</td>
                  <td className="py-3 px-4 text-center text-[#4A5568] text-sm">{row.starter}</td>
                  <td className="py-3 px-4 text-center text-[#2D3748] text-sm font-medium">{row.growth}</td>
                  <td className="py-3 px-4 text-center text-[#4A5568] text-sm">{row.pro}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Comparison Cards - Mobile */}
        <div className="mt-16 md:hidden">
          <h3 className="text-2xl font-bold text-[#2D3748] text-center mb-8">Feature Comparison</h3>
          <div className="space-y-4">
            {comparison.map((row, i) => (
              <div key={i} className="bg-gray-50 rounded-xl p-4">
                <p className="text-[#2D3748] font-semibold text-sm mb-3">{row.feature}</p>
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div><p className="text-xs text-[#4A5568] mb-1">Starter</p><p className="text-sm text-[#2D3748]">{row.starter}</p></div>
                  <div className="bg-[#F5A623]/10 rounded-lg py-1"><p className="text-xs text-[#F5A623] mb-1">Growth</p><p className="text-sm text-[#2D3748] font-medium">{row.growth}</p></div>
                  <div><p className="text-xs text-[#4A5568] mb-1">Pro</p><p className="text-sm text-[#2D3748]">{row.pro}</p></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── FINAL CTA ───
function FinalCTA() {
  return (
    <section className="relative py-20 sm:py-28 overflow-hidden">
      <div className="absolute inset-0 overflow-hidden pointer-events-none opacity-30">
        <div className="absolute inset-0 bg-gradient-to-b from-[#87CEEB] via-[#B8DFF5] to-white" />
        <Cloud className="absolute w-[220px] sm:w-[280px] top-[8%] left-[2%] animate-float-cloud opacity-95" />
        <SmallCloud className="absolute w-[160px] sm:w-[200px] top-[12%] right-[5%] animate-float-cloud-slow opacity-90" />
        <ThinCloud className="absolute w-[140px] sm:w-[180px] top-[28%] left-[35%] animate-float-cloud opacity-60" style={{ animationDelay: '5s' }} />
        <Cloud className="absolute w-[200px] sm:w-[260px] top-[50%] right-[15%] animate-float-cloud-slow opacity-50" style={{ animationDelay: '8s' }} />
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="flex flex-col items-center mb-8">
          <OptImage src={`${IMAGES_BASE}/bee-community-v2-nobg.png`} alt="Biz Bee and her community" width={384} height={224} className="object-contain drop-shadow-lg w-72 h-44 sm:w-96 sm:h-56" />
        </div>
        <div className="text-center">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-[#2D3748] mb-6">Your Clients Are Forgetting About You Right Now.</h2>
          <p className="text-xl text-[#4A5568] mb-8 max-w-2xl mx-auto leading-relaxed">Start your newsletter today. Five minutes a week is all it takes to stay top-of-mind with the people who matter most to your business.</p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-8">
            <Link to="/signup" className="inline-flex items-center justify-center whitespace-nowrap px-6 sm:px-10 py-3 sm:py-4 bg-gradient-to-r from-[#F5A623] to-[#FFD700] text-[#2D3748] font-bold rounded-full text-base sm:text-lg hover:shadow-xl hover:shadow-[#F5A623]/25 transition-all duration-300 hover:-translate-y-0.5">
              Start Your Newsletter — $29/mo
              <ArrowRight className="ml-2 w-5 h-5" />
            </Link>
          </div>
          <p className="text-sm text-[#4A5568]">30-day money-back guarantee · Cancel anytime</p>
        </div>
      </div>
    </section>
  );
}

// ─── FOOTER ───
function Footer() {
  return (
    <footer className="bg-[#2D3748] text-white/60 py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row justify-between items-center gap-6">
          <div className="flex items-center gap-2">
            <img src={`${IMAGES_BASE}/logo-192.png`} alt="BizBuzz" width={32} height={32} />
            <span className="text-white font-bold text-xl">BizBuzz</span>
          </div>
          <div className="flex flex-wrap items-center gap-6 text-sm">
            <a href="https://bizbuzzapp.com/privacy" className="hover:text-white transition-colors">Privacy</a>
            <a href="https://bizbuzzapp.com/terms" className="hover:text-white transition-colors">Terms</a>
            <a href="https://bizbuzzapp.com/refund" className="hover:text-white transition-colors">Refund Policy</a>
            <a href="mailto:sales@bizbuzzapp.com" className="hover:text-white transition-colors">Contact</a>
          </div>
          <p className="text-sm">© {new Date().getFullYear()} BizBuzz. All rights reserved.</p>
        </div>
      </div>
    </footer>
  );
}

// ─── MAIN LANDING PAGE ───
export default function LandingPage() {
  return (
    <div className="min-h-screen">
      {/* Sticky Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b border-gray-200/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16">
          <div className="flex items-center gap-2">
            <img src={`${IMAGES_BASE}/logo-192.png`} alt="BizBuzz" width={32} height={32} />
            <span className="text-[#2D3748] font-bold text-xl">BizBuzz</span>
          </div>
          <nav className="hidden md:flex items-center gap-8 text-sm text-[#4A5568] font-medium">
            <a href="#features" className="hover:text-[#F5A623] transition-colors">Features</a>
            <a href="#how-it-works" className="hover:text-[#F5A623] transition-colors">How It Works</a>
            <a href="#pricing" className="hover:text-[#F5A623] transition-colors">Pricing</a>
          </nav>
          <div className="flex items-center gap-3">
            <Link to="/login" className="text-sm font-medium text-[#4A5568] hover:text-[#2D3748] transition-colors">Log In</Link>
            <Link to="/signup" className="text-sm font-semibold px-4 py-2 bg-gradient-to-r from-[#F5A623] to-[#FFD700] text-[#2D3748] rounded-full hover:shadow-md transition-all">Get Started</Link>
          </div>
        </div>
      </header>

      <main className="pt-16">
        <Hero />
        <ProblemSection />
        <FeaturesSection />
        <HowItWorks />
        <BuiltFor />
        <PricingSection />
        <FinalCTA />
      </main>

      <Footer />
    </div>
  );
}
